import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { eventTargetIsEditable, isHubQuickSearchGloballyBlocked } from '../utils/isEditableTarget';

export type ProjectHubViewMode = 'table' | 'gantt' | 'files' | 'suppliers' | 'changeOrders' | 'crew';

/** Letter keys — only active when a single project is selected in Project Hub. */
export const PROJECT_HUB_VIEW_MODE_HOTKEYS: ReadonlyArray<{
  key: string;
  mode: ProjectHubViewMode;
  label: string;
}> = [
  { key: 't', mode: 'table', label: 'Table' },
  { key: 'f', mode: 'files', label: 'Files' },
  { key: 's', mode: 'suppliers', label: 'Suppliers' },
  { key: 'c', mode: 'changeOrders', label: 'Change Orders' },
  { key: 'r', mode: 'crew', label: 'Crew' },
  { key: 'l', mode: 'gantt', label: 'Timeline' },
];

export function projectHubViewModeForHotkey(
  key: string,
  opts: { canAccessSuppliersTab: boolean; canAccessChangeOrdersTab: boolean },
): ProjectHubViewMode | null {
  const entry = PROJECT_HUB_VIEW_MODE_HOTKEYS.find(h => h.key === key.toLowerCase());
  if (!entry) return null;
  if (entry.mode === 'suppliers' && !opts.canAccessSuppliersTab) return null;
  if (entry.mode === 'changeOrders' && !opts.canAccessChangeOrdersTab) return null;
  return entry.mode;
}

export function projectHubViewModeHotkeyLabel(mode: ProjectHubViewMode): string | null {
  const entry = PROJECT_HUB_VIEW_MODE_HOTKEYS.find(h => h.mode === mode);
  return entry ? entry.key.toUpperCase() : null;
}

/** Query value for `/project-hub/:id?tab=` — matches the hub view-mode keys. */
export function projectHubViewModeTabParam(mode: ProjectHubViewMode): string {
  return mode;
}

export function projectHubViewPath(projectId: string, mode: ProjectHubViewMode): string {
  return `/project-hub/${projectId}?tab=${projectHubViewModeTabParam(mode)}`;
}

/** Files tab opened on the Project folders view with a folder selected (`#files-…` comment chips). */
export function projectHubFilesFolderPath(projectId: string, folderPath: string): string {
  return `${projectHubViewPath(projectId, 'files')}&view=folders&folder=${encodeURIComponent(folderPath)}`;
}

/** Billing & Payables, scrolled to this project's deal. */
export function projectHubBillingPath(dealId: string): string {
  return `/billing-payables?deal=${encodeURIComponent(dealId)}`;
}

export const PROJECT_HUB_BILLING_HOTKEY = 'b';

export function isProjectHubBillingHotkey(e: KeyboardEvent): boolean {
  return e.code === 'KeyB' || e.key.toLowerCase() === PROJECT_HUB_BILLING_HOTKEY;
}

/** Shared gate for T/F/S/C/L/B/P/I/H/1 — skip modifiers, overlays, and fields. */
export function shouldHandleProjectHubLetterHotkey(e: KeyboardEvent): boolean {
  if (e.altKey || e.ctrlKey || e.metaKey || e.repeat) return false;
  if (isHubQuickSearchGloballyBlocked()) return false;
  if (eventTargetIsEditable(e)) return false;
  return true;
}

/** Accepts `gantt` / `timeline` and the other view-mode tab names. */
export function projectHubViewModeFromTabParam(tab: string | null): ProjectHubViewMode | null {
  if (!tab) return null;
  if (tab === 'timeline' || tab === 'gantt') return 'gantt';
  if (
    tab === 'table' ||
    tab === 'files' ||
    tab === 'suppliers' ||
    tab === 'changeOrders' ||
    tab === 'crew'
  ) {
    return tab;
  }
  return null;
}

export interface UseProjectHubViewModeHotkeysOptions {
  enabled?: boolean;
  selectedProjectId: string | null;
  canAccessSuppliersTab: boolean;
  canAccessChangeOrdersTab: boolean;
  onViewModeChange: (mode: ProjectHubViewMode) => void;
}

/** Switch Project Hub tabs with T/F/S/C/L (when not typing in a field). */
export function useProjectHubViewModeHotkeys({
  enabled = true,
  selectedProjectId,
  canAccessSuppliersTab,
  canAccessChangeOrdersTab,
  onViewModeChange,
}: UseProjectHubViewModeHotkeysOptions): void {
  useEffect(() => {
    if (!enabled || !selectedProjectId) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!shouldHandleProjectHubLetterHotkey(e)) return;

      const mode = projectHubViewModeForHotkey(e.key, {
        canAccessSuppliersTab,
        canAccessChangeOrdersTab,
      });
      if (!mode) return;

      e.preventDefault();
      onViewModeChange(mode);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    enabled,
    selectedProjectId,
    canAccessSuppliersTab,
    canAccessChangeOrdersTab,
    onViewModeChange,
  ]);
}

export interface UseProjectHubBillingHotkeyOptions {
  enabled?: boolean;
  dealId: string | null | undefined;
  onOpenBilling: (dealId: string) => void;
}

/**
 * B on any single-project hub tab (Table / Files / Suppliers / COs / Timeline)
 * jumps to Billing & Payables for that deal. Capture-phase so tab UIs cannot
 * swallow the letter the way the old bubble listener did.
 */
export function useProjectHubBillingHotkey({
  enabled = true,
  dealId,
  onOpenBilling,
}: UseProjectHubBillingHotkeyOptions): void {
  useEffect(() => {
    if (!enabled || !dealId) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!shouldHandleProjectHubLetterHotkey(e)) return;
      if (!isProjectHubBillingHotkey(e)) return;
      e.preventDefault();
      onOpenBilling(dealId);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, dealId, onOpenBilling]);
}

export function isProjectHubProposalHotkey(e: KeyboardEvent): boolean {
  return e.code === 'KeyP' || e.key.toLowerCase() === 'p';
}

export function isProjectHubInspirationHotkey(e: KeyboardEvent): boolean {
  return e.code === 'KeyI' || e.key.toLowerCase() === 'i';
}

export function isProjectHubLatestCommentHotkey(e: KeyboardEvent): boolean {
  return e.key === '1' || e.code === 'Digit1' || e.code === 'Numpad1';
}

export interface UseProjectHubExternalNavHotkeysOptions {
  enabled?: boolean;
  onProposal?: () => void;
  onInspiration?: () => void;
}

/**
 * Single-project hub: P opens the proposal, I the inspiration board.
 * Capture-phase so table/gantt UIs cannot swallow the keys. All-projects view keeps P/I.
 * Proposal builder P (see useProposalBuilderProjectHubHotkey) returns to the hub table.
 */
export function useProjectHubExternalNavHotkeys({
  enabled = true,
  onProposal,
  onInspiration,
}: UseProjectHubExternalNavHotkeysOptions): void {
  useEffect(() => {
    if (!enabled) return;
    if (!onProposal && !onInspiration) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!shouldHandleProjectHubLetterHotkey(e)) return;
      if (onProposal && isProjectHubProposalHotkey(e)) {
        e.preventDefault();
        onProposal();
        return;
      }
      if (onInspiration && isProjectHubInspirationHotkey(e)) {
        e.preventDefault();
        onInspiration();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, onProposal, onInspiration]);
}

export interface UseProjectHubLatestCommentHotkeyOptions {
  enabled?: boolean;
  onOpen: () => void;
}

/** Single-project hub: 1 opens the comment group whose thread has the newest comment. */
export function useProjectHubLatestCommentHotkey({
  enabled = true,
  onOpen,
}: UseProjectHubLatestCommentHotkeyOptions): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!shouldHandleProjectHubLetterHotkey(e)) return;
      if (!isProjectHubLatestCommentHotkey(e)) return;
      e.preventDefault();
      onOpen();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, onOpen]);
}

export interface UseProposalBuilderProjectHubHotkeyOptions {
  enabled?: boolean;
  projectId: string | null | undefined;
  onOpenHub: () => void;
}

/**
 * Proposal builder: P opens that deal's Project Hub table — the reverse of hub P.
 * Same letter-hotkey gate so typing in cost fields does not navigate away.
 */
export function useProposalBuilderProjectHubHotkey({
  enabled = true,
  projectId,
  onOpenHub,
}: UseProposalBuilderProjectHubHotkeyOptions): void {
  useEffect(() => {
    if (!enabled || !projectId) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!shouldHandleProjectHubLetterHotkey(e)) return;
      if (!isProjectHubProposalHotkey(e)) return;
      e.preventDefault();
      onOpenHub();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, projectId, onOpenHub]);
}

export const PROJECT_HUB_ALL_PROJECTS_HOTKEY = 'h';
export const PROJECT_HUB_ALL_PROJECTS_PATH = '/project-hub';

export function isProjectHubAllProjectsHotkey(e: KeyboardEvent): boolean {
  return e.code === 'KeyH' || e.key.toLowerCase() === PROJECT_HUB_ALL_PROJECTS_HOTKEY;
}

/** True when the URL is already the hub list (no selected project). */
export function isAllProjectsHubPath(pathname: string): boolean {
  const trimmed = pathname.replace(/\/+$/, '') || '/';
  return trimmed === PROJECT_HUB_ALL_PROJECTS_PATH;
}

/** `/project-hub`, keeping supplier preview `viewAs` / `as` when present. */
export function projectHubAllProjectsPath(search?: string): string {
  const params = new URLSearchParams(search || '');
  const viewAs = params.get('viewAs')?.trim();
  if (!viewAs) return PROJECT_HUB_ALL_PROJECTS_PATH;
  const next = new URLSearchParams();
  next.set('viewAs', viewAs);
  const previewRole = params.get('as');
  if (previewRole) next.set('as', previewRole);
  return `${PROJECT_HUB_ALL_PROJECTS_PATH}?${next.toString()}`;
}

export interface UseGlobalProjectHubHotkeyOptions {
  enabled?: boolean;
}

/**
 * CRM-wide H: jump to Project Hub all-projects. Capture-phase so page UIs
 * cannot swallow the letter. Same typing / overlay gate as other hub letters.
 */
export function useGlobalProjectHubHotkey({
  enabled = true,
}: UseGlobalProjectHubHotkeyOptions = {}): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!shouldHandleProjectHubLetterHotkey(e)) return;
      if (!isProjectHubAllProjectsHotkey(e)) return;
      if (isAllProjectsHubPath(location.pathname)) return;
      e.preventDefault();
      navigate(projectHubAllProjectsPath(location.search));
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, navigate, location.pathname, location.search]);
}
